import { useRef, useImperativeHandle, forwardRef, useCallback } from 'react';
import gsap from 'gsap';
import { Draggable } from 'gsap/Draggable';
import { InertiaPlugin } from 'gsap/InertiaPlugin';
import { useGSAP } from '@gsap/react';
import CardFace from '../ArcanaCard/CardFace';
import { type ArcanaIdentityIndex } from '../../constants/arcana-identities';
import './CardCarousel.css';
import type { CarouselDraggableSnapHandle } from './CardCarouselDraggableSnapHandle';
import { reverseCustomEasePath } from '../../utilities/reverse-ease';
import { CustomEase } from 'gsap/CustomEase';

gsap.registerPlugin(Draggable, InertiaPlugin, CustomEase);

const CARD_SIZE_RATIO = 1.5; // height = width * 1.5

interface CardCarouselProps {
    cardIDs: ArcanaIdentityIndex[];
    cardGapInPx: number;
    startingIndex: number;
    onIndexChange?: (index: number) => void;
    cardWidth?: number;
    cardHeight?: number;
    onDragStart?: (direction: 1 | -1) => void;
    onDragComplete?: (index: number) => void;
    /** Fires when a card is clicked (after the snap-to-index behaviour). */
    onCardClick?: (index: number) => void;
    animations?: (
        | { property: string; type?: 'bell'; peak: number; trough: number; ease?: string }
        | { property: string; type: 'linear'; left: number; right: number; ease?: string }
    )[];
    disable3d?: boolean;
    compressImages?: boolean;
    deadzoneEnabled?: boolean;
}

const CardCarousel = forwardRef<CarouselDraggableSnapHandle, CardCarouselProps>(
    function CarouselDraggableSnapTest({ cardIDs, onIndexChange, cardHeight, cardWidth, cardGapInPx, onDragStart, onDragComplete, onCardClick, startingIndex, 
        animations = [], disable3d = false, compressImages = false, deadzoneEnabled = true}, ref) {

        if (cardWidth === undefined && cardHeight === undefined) {
            throw new Error('CardCarousel: either cardWidth or cardHeight must be provided');
        }

        const resolvedCardHeight = cardHeight ?? Math.round(cardWidth! * CARD_SIZE_RATIO);
        const resolvedCardWidth = cardWidth ?? Math.round(cardHeight! / CARD_SIZE_RATIO);

        const wrapperRef = useRef<HTMLDivElement>(null);
        const loopRef = useRef<any>(null);
        const onIndexChangeRef = useRef(onIndexChange);
        onIndexChangeRef.current = onIndexChange;

        const onDragStartRef = useRef(onDragStart);
        onDragStartRef.current = onDragStart;

        const onDragCompleteRef = useRef(onDragComplete);
        onDragCompleteRef.current = onDragComplete;

        const onCardClickRef = useRef(onCardClick);
        onCardClickRef.current = onCardClick;

        const next = useCallback(() => {
            loopRef.current?.next({ duration: 0.4, ease: "power1.inOut" });
            const idx = loopRef.current?.current() ?? 0;
            onIndexChangeRef.current?.(idx);
        }, []);

        const previous = useCallback(() => {
            loopRef.current?.previous({ duration: 0.4, ease: "power1.inOut" });
            const idx = loopRef.current?.current() ?? 0;
            onIndexChangeRef.current?.(idx);
        }, []);

        const toggleOverflow = useCallback(() => {
            wrapperRef.current?.classList.toggle("show-overflow");
        }, []);

        const toIndex = useCallback((index: number) => {
            loopRef.current?.toIndex(index, { duration: 0.4, ease: "power1.inOut" });
        }, []);

        const current = useCallback(() => {
            return loopRef.current?.current() ?? 0;
        }, []);

        useImperativeHandle(ref, () => ({
            next,
            previous,
            toggleOverflow,
            toIndex,
            current,
        }), [next, previous, toggleOverflow, toIndex, current]);

        useGSAP(() => {
            const cards = gsap.utils.toArray<HTMLElement>(".card");
            if (!cards.length) return;

            gsap.set(cards, { width: resolvedCardWidth, height: resolvedCardHeight });

            const middleCard = cards[Math.floor(cards.length / 2)] as HTMLElement;
            const offset = middleCard.offsetLeft + middleCard.offsetWidth / 2;

            wrapperRef.current!.style.transform = `translateX(calc(50vw - ${offset}px))`;
            wrapperRef.current!.style.perspectiveOrigin = `${offset}px 50%`;

            const centreIndex = Math.floor(cards.length / 2) ?? 0;
            const centreOffset = centreIndex / cards.length;
            const loop = horizontalLoop(cards, {
                paused: true,
                draggable: true,
                paddingRight: cardGapInPx,
                startIndex: centreIndex,
                onIndexChange: (idx: number) => onIndexChangeRef.current?.(idx),
                onDragStart: (direction: 1 | -1) => onDragStartRef.current?.(direction),
                onDragComplete: (idx: number) => onDragCompleteRef.current?.(idx),
            });
            // Animate each card as it moves across its own relative centre point of the screen.
            const totalDuration = loop.duration();

            // Capture BEFORE mutation
            const peakProgresses = cards.map((_: HTMLElement, i: number) => loop.times[i] / totalDuration);

            loop.times.forEach((_: number, i: number) => {
                loop.times[i] = gsap.utils.wrap(0, loop.duration(), loop.times[i] + centreOffset * loop.duration());
            });
            loop.progress(centreOffset, true);

            // After building the loop, build a separate scale timeline
            console.debug('Setting up CardCarousel animations with config:', { animations });
            const reverseEase = (e: string): string => {
                if (e === 'none') return 'none';
                if (e.endsWith('.out')) return e.replace('.out', '.in');
                if (e.endsWith('.in')) return e.replace('.in', '.out');
                if (e.startsWith('M')) return reverseCustomEasePath(e);
                throw new Error(`Invalid ease: "${e}". Must end in '.in' or '.out' (e.g. 'power2.in') or be a CustomEase SVG path as a string starting with 'M'.`);
            };

            // Parse ease functions ONCE, outside the hot path
            let parsedAnimations: any[] = [];
            try {
                parsedAnimations = animations.map((anim) => {
                    if (anim.type === 'linear') {
                        const easeFn = gsap.parseEase(
                            anim.ease?.startsWith('M') ? CustomEase.create('ease_linear', anim.ease) : (anim.ease ?? 'none')
                        );
                        return { type: 'linear', property: anim.property, left: anim.left, right: anim.right, easeFn };
                    }
                    // existing bell curve logic
                    const { property, peak, trough, ease = 'none' } = anim;
                    const fwdEaseFn = gsap.parseEase(ease.startsWith('M') ? CustomEase.create('ease_fwd_global', ease) : ease);
                    const revEaseFn = gsap.parseEase(ease.startsWith('M') ? CustomEase.create('ease_rev_global', reverseCustomEasePath(ease)) : reverseEase(ease));
                    return { type: 'bell', property, peak, trough, fwdEaseFn, revEaseFn };
                });

            } catch (e) {
                console.error('parsedAnimations setup threw:', e);
            }

            // Create one quickSetter per card per animated property
            const cardAnimators = cards.map((card: HTMLElement, i: number) => ({
                peakProgress: peakProgresses[i],
                cssSet: gsap.quickSetter(card, 'css') as (v: object) => void,
                atTrough: false,
                props: Object.fromEntries(parsedAnimations.map(({ property, trough }) => [property, trough])) as Record<string, number>,
            }));

            const troughProps = Object.fromEntries(
                parsedAnimations
                    .filter(a => a.type !== 'linear')
                    .map(({ property, trough }) => [property, trough])
            );

            const approxTotalWidth = cards.length * (resolvedCardWidth + cardGapInPx);
            const DEADZONE = deadzoneEnabled ? Math.max(0, 0.5 - (window.innerWidth / 2 + resolvedCardWidth) / approxTotalWidth) : 0;
            const upperDeadzone = 1 - DEADZONE;

            const syncScales = () => {
                try {
                    const base = loop.progress() - centreOffset + 0.5;
                    for (let i = 0; i < cardAnimators.length; i++) {
                        const animator = cardAnimators[i];
                        const tlProgress = (((base - animator.peakProgress) % 1) + 1) % 1;

                        if (tlProgress < DEADZONE || tlProgress > upperDeadzone) {
                            if (!animator.atTrough) {
                                animator.cssSet(troughProps);
                                animator.atTrough = true;
                            }

                            let hasLinear = false;
                            for (let j = 0; j < parsedAnimations.length; j++) {
                                const anim = parsedAnimations[j];
                                if (anim.type === 'linear') {
                                    animator.props[anim.property] = anim.left + (anim.right - anim.left) * anim.easeFn(tlProgress);
                                    hasLinear = true;
                                }
                            }
                            if (hasLinear) animator.cssSet(animator.props);
                            continue;
                        }

                        animator.atTrough = false;
                        for (let j = 0; j < parsedAnimations.length; j++) {
                            const anim = parsedAnimations[j];
                            if (anim.type === 'linear') {
                                animator.props[anim.property] = anim.left + (anim.right - anim.left) * anim.easeFn(tlProgress);
                            } else {
                                const t2 = tlProgress * 2;
                                const t2s = (tlProgress - 0.5) * 2;
                                animator.props[anim.property] = tlProgress <= 0.5
                                    ? anim.trough + (anim.peak - anim.trough) * anim.fwdEaseFn(t2)
                                    : anim.peak + (anim.trough - anim.peak) * anim.revEaseFn(t2s);
                            }
                        }
                        animator.cssSet(animator.props);
                    }
                } catch (e) {
                    console.error('syncScales setup threw:', e);
                }
            };
            console.debug('CardCarousel setup complete. Loop and animations are configured. calling syncScales for the first time to set initial states.');
            loop.eventCallback("onUpdate", syncScales);
            syncScales();
            console.debug('parsedAnimations:', parsedAnimations);

            loop.progress(centreOffset, true);
            loopRef.current = loop;

            if (startingIndex !== 0) {
                loop.jumpToIndex(startingIndex);
                syncScales();
            }

            const clickHandlers = cards.map((card, i) => {
                const handler = () => {
                    loop.toIndex(i, { duration: 0.8, ease: 'power1.inOut' });
                    onCardClickRef.current?.(i);
                };
                card.addEventListener("click", handler);
                return { card, handler };
            });

            // Return cleanup — runs when component unmounts
            return () => {
                clickHandlers.forEach(({ card, handler }) =>
                    card.removeEventListener("click", handler)
                );
            };

        }, { scope: wrapperRef });

        console.debug('syncScales complete. Now Rendering CardCarousel with props:', { resolvedCardHeight, resolvedCardWidth, cardGapInPx, animations });
        return (
            <div style={{ perspective: '800px', perspectiveOrigin: '50vw 50%', width: '100%' }}>
                <div ref={wrapperRef} className="wrapper" data-preserve-3d={!disable3d ? "true" : undefined}>
                    {cardIDs.map((cardId, index) => (
                        <div key={index} className="card" style={{ marginRight: cardGapInPx }}>
                            <CardFace
                                cardId={cardId}
                                cardWidth={resolvedCardWidth}
                                cardHeight={resolvedCardHeight}
                                isOptimised={compressImages}
                            />
                        </div>
                    ))}
                </div>
            </div>
        );
    });

export default CardCarousel;

function setMiddle(boxes: HTMLElement[], mid: number) {
    boxes.forEach((box) => box.classList.remove("middle-item"));
    const wrap = gsap.utils.wrap(0, boxes.length);
    boxes[wrap(mid)].classList.add("middle-item");
}

function horizontalLoop(items: any, config: any) {
    let externalNavActive = false;
    items = gsap.utils.toArray(items);
    config = config || {};
    let hasFiredDragStart = false;
    let tl: any = gsap.timeline({
        repeat: config.repeat,
        paused: config.paused,
        defaults: { ease: "none" },
        onReverseComplete: () => tl.totalTime(tl.rawTime() + tl.duration() * 100)
    }),
        length = items.length,
        startX = items[0].offsetLeft,
        times: number[] = [],
        widths: number[] = [],
        xPercents: number[] = [],
        curIndex = 0,
        pixelsPerSecond = (config.speed || 1) * 100,
        snap = config.snap === false ? (v: number) => v : gsap.utils.snap(config.snap || 1),
        populateWidths = () =>
            items.forEach((el: HTMLElement, i: number) => {
                widths[i] = parseFloat(gsap.getProperty(el, "width", "px") as string);
                xPercents[i] = snap(
                    (parseFloat(gsap.getProperty(el, "x", "px") as string) / widths[i]) * 100 +
                    (gsap.getProperty(el, "xPercent") as number)
                );
            }),
        getTotalWidth = () =>
            items[length - 1].offsetLeft +
            (xPercents[length - 1] / 100) * widths[length - 1] -
            startX +
            items[length - 1].offsetWidth *
            (gsap.getProperty(items[length - 1], "scaleX") as number) +
            (parseFloat(config.paddingRight) || 0),
        totalWidth: number,
        curX: number,
        distanceToStart: number,
        distanceToLoop: number,
        item: HTMLElement,
        i: number;

    populateWidths();
    gsap.set(items, { xPercent: (i: number) => xPercents[i] });
    gsap.set(items, { x: 0 });
    totalWidth = getTotalWidth();

    for (i = 0; i < length; i++) {
        item = items[i];
        curX = (xPercents[i] / 100) * widths[i];
        distanceToStart = item.offsetLeft + curX - startX;
        distanceToLoop = distanceToStart + widths[i] * (gsap.getProperty(item, "scaleX") as number);
        tl.to(item, {
            xPercent: snap(((curX - distanceToLoop) / widths[i]) * 100),
            duration: distanceToLoop / pixelsPerSecond
        }, 0)
            .fromTo(item, {
                xPercent: snap(((curX - distanceToLoop + totalWidth) / widths[i]) * 100),
            }, {
                xPercent: xPercents[i],
                duration: (curX - distanceToLoop + totalWidth - curX) / pixelsPerSecond,
                immediateRender: false
            }, distanceToLoop / pixelsPerSecond)
            .add("label" + i, distanceToStart / pixelsPerSecond);
        times[i] = distanceToStart / pixelsPerSecond;
    }

    let navGeneration = 0;

    function toIndex(index: number, vars: any) {
        vars = vars || {};
        Math.abs(index - curIndex) > length / 2 &&
            (index += index > curIndex ? -length : length);
        let newIndex = gsap.utils.wrap(0, length, index),
            time = times[newIndex];
        if (newIndex === curIndex) return; // guard first
        externalNavActive = true;         // only set if we're actually navigating
        if (time > tl.time() !== index > curIndex) {
            vars.modifiers = { time: gsap.utils.wrap(0, tl.duration()) };
            time += tl.duration() * (index > curIndex ? 1 : -1);
        }
        curIndex = newIndex;
        vars.overwrite = true;
        const generation = ++navGeneration;
        vars.onComplete = () => {
            if (navGeneration !== generation) return;
            //console.log(`Carousel movement complete (toIndex.onComplete). Landed on index ${curIndex}`);
            config.onIndexChange?.(curIndex);
        };
        return tl.tweenTo(time, vars);
    }

    tl.next = (vars: any) => { toIndex(curIndex + 1, vars) && setMiddle(items, tl.current() + 2); };
    tl.previous = (vars: any) => { toIndex(curIndex - 1, vars) && setMiddle(items, tl.current() + 2); };
    tl.current = () => curIndex;
    tl.toIndex = (index: number, vars: any) => toIndex(index, vars);
    tl.updateIndex = () => (curIndex = gsap.utils.wrap(0, length, Math.round((tl.progress() - ((config.startIndex || 0) / length)) * length)));
    tl.times = times;
    tl.progress(1, true).progress(0, true);
    tl.jumpToIndex = (index: number) => {
        const targetTime = times[gsap.utils.wrap(0, length, index)];
        curIndex = gsap.utils.wrap(0, length, index);
        tl.progress(gsap.utils.wrap(0, 1, targetTime / tl.duration()), true);
    };

    if (config.reversed) {
        tl.vars.onReverseComplete();
        tl.reverse();
    }

    if (config.draggable && typeof Draggable === "function") {
        let proxy = document.createElement("div"),
            wrap = gsap.utils.wrap(0, 1),
            ratio: number,
            startProgress: number,
            draggable: any,
            dragSnap: number,
            roundFactor: number,
            align = () => {
                if (externalNavActive) return;
                tl.progress(wrap(startProgress + (draggable.startX - draggable.x) * ratio));
            },
            syncIndex = () => tl.updateIndex();

        typeof InertiaPlugin === "undefined" &&
            console.warn("InertiaPlugin required for momentum-based scrolling and snapping. https://greensock.com/club");

        draggable = Draggable.create(proxy, {
            trigger: items[0].parentNode,
            type: "x",
            onPress() {
                if (draggable.isThrowing) draggable.endDrag();
                externalNavActive = false;
                hasFiredDragStart = false;
                tl.pause()
                startProgress = tl.progress();
                totalWidth = getTotalWidth();
                ratio = 1 / totalWidth;
                dragSnap = totalWidth / items.length;
                roundFactor = Math.pow(10, ((dragSnap + "").split(".")[1] || "").length);
            },
            onDrag() {
                if (!hasFiredDragStart) {
                    hasFiredDragStart = true;
                    const direction = draggable.deltaX < 0 ? 1 : -1;
                    config.onDragStart?.(direction);
                }
                align();
            },
            onThrowUpdate: align,
            inertia: true,
            snap: (value: number) => {
                const startOffset = ((config.startIndex || 0) / length) * totalWidth;
                let n = Math.round((parseFloat(value as any) - startOffset) / dragSnap) * dragSnap * roundFactor;
                return (n - (n % 1)) / roundFactor + startOffset;
            },
            onRelease: syncIndex,
            onThrowComplete: () => {
                console.debug(`Carousel stopped moving towards a destination. (onThrowComplete has fired) - externalNavActive: ${externalNavActive}`);
                gsap.set(proxy, { x: 0 });
                const wasExternal = externalNavActive;
                externalNavActive = false;
                if (wasExternal) return;
                syncIndex();
                tl.progress(gsap.utils.wrap(0, 1, times[curIndex] / tl.duration()), true);
                setMiddle(items, tl.current() + 2);
                config.onDragComplete?.(tl.current());
            }
        })[0];

        tl.draggable = draggable;
    }

    return tl;
}
